/**
 * Items, their triggers, and the service log (§5).
 *
 * Items are owner-only to create/edit. Service logging is owner-*or-crew*:
 * that split is the whole point of the crew role — they run the weekend, they
 * just don't restructure the fleet (§2, Access model).
 */

import { z } from 'zod';

import { UNITS_BY_TRIGGER, ownerType, system, triggerType, triggerUnit } from './enums.js';
import { count, intervalValue, isoDate, optionalHttpUrl, optionalText, shortText, uuid } from './primitives.js';

/**
 * One check condition. Refined so the unit always suits the type — the engine
 * throws on a `time` trigger measured in passes, and a 422 here beats a 500
 * there.
 */
export const triggerInput = z
  .object({
    type: triggerType,
    intervalValue,
    unit: triggerUnit,
    lastCheckedAt: isoDate.nullable().optional(),
  })
  .superRefine((t, ctx) => {
    const allowed = UNITS_BY_TRIGGER[t.type];
    if (!allowed.includes(t.unit)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unit'],
        message: `A "${t.type}" trigger must be measured in ${allowed.join(' | ')}, not "${t.unit}"`,
      });
    }
    if (t.lastCheckedAt && t.type !== 'inspect') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lastCheckedAt'],
        message: 'Only an "inspect" trigger tracks when it was last checked',
      });
    }
  });
export type TriggerInput = z.infer<typeof triggerInput>;

export const createItemRequest = z
  .object({
    ownerType,
    ownerId: uuid,
    name: shortText(80),
    partNumber: optionalText(60),
    system,
    buyUrl: optionalHttpUrl,
    isSafety: z.boolean().default(false),
    expirationDate: isoDate.nullable().optional(),
    spec: optionalText(60),
    lastServicedAt: isoDate.nullable().optional(),
    /**
     * Zero is allowed: a **catalog** item (a sensor, a bracket) is recorded for
     * reference — its part number and buy URL, so a failure is easy to reorder —
     * without being tracked. It simply never comes due, which the engine already
     * handles by projecting nothing. A tracked part carries one to six.
     */
    triggers: z.array(triggerInput).max(6),
  })
  .superRefine((item, ctx) => {
    // At most one trigger of a kind: two "every N passes" rules on one part
    // would silently race, and the engine reads only the first.
    const seen = new Set<string>();
    item.triggers.forEach((t, i) => {
      if (seen.has(t.type)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['triggers', i, 'type'],
          message: `This item already has a "${t.type}" condition`,
        });
      }
      seen.add(t.type);
    });

    if (item.expirationDate && !item.isSafety) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expirationDate'],
        message: 'Only safety gear carries an expiration date',
      });
    }

    // A time trigger with nothing to count from projects nothing at all, which
    // would look like a silently broken part.
    const hasTime = item.triggers.some((t) => t.type === 'time');
    if (hasTime && !item.lastServicedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lastServicedAt'],
        message: 'A time-based interval needs a last-serviced date to count from',
      });
    }
  });
export type CreateItemRequest = z.infer<typeof createItemRequest>;

/**
 * Bulk create — several items in one request, created atomically server-side.
 *
 * The "Add usual safety equipment" starter adds a whole kit at once. Doing that
 * as N separate POSTs made the browser fire concurrent writes, which Aurora DSQL
 * aborts under optimistic concurrency (all but one conflict). One request, one
 * transaction, keeps that concurrency concern behind the API where it belongs.
 * Capped so a single call can't be turned into an unbounded write.
 */
export const createItemsRequest = z.object({
  items: z.array(createItemRequest).min(1).max(20),
});
export type CreateItemsRequest = z.infer<typeof createItemsRequest>;

/**
 * Update. Built from the object *inside* the refinement, because a refined
 * schema cannot be `.partial()`-ed — and a partial update genuinely has
 * different cross-field rules (the API re-checks them against the merged row).
 */
export const updateItemRequest = z.object({
  name: shortText(80).optional(),
  partNumber: optionalText(60),
  system: system.optional(),
  buyUrl: optionalHttpUrl,
  isSafety: z.boolean().optional(),
  expirationDate: isoDate.nullable().optional(),
  spec: optionalText(60),
  lastServicedAt: isoDate.nullable().optional(),
  // Zero allowed on edit too — a tracked part can be demoted to a catalog entry.
  triggers: z.array(triggerInput).max(6).optional(),
});
export type UpdateItemRequest = z.infer<typeof updateItemRequest>;

/**
 * `POST /items/:id/service-logs` — mark a service done. Resets the counters.
 * Owner or crew.
 */
export const createServiceLogRequest = z.object({
  servicedAt: isoDate,
  atPasses: count.nullable().optional(),
  atMiles: count.nullable().optional(),
  eventId: uuid.nullable().optional(),
  cost: z.number().min(0).max(1_000_000).nullable().optional(),
  notes: optionalText(500),
});
export type CreateServiceLogRequest = z.infer<typeof createServiceLogRequest>;

/** `POST /items/:id/checked` — the inspect card's "mark checked today". */
export const markCheckedRequest = z.object({
  checkedAt: isoDate,
});
export type MarkCheckedRequest = z.infer<typeof markCheckedRequest>;

export const itemParam = z.object({ garageId: uuid, itemId: uuid });
export const serviceLogParam = z.object({ garageId: uuid, itemId: uuid, logId: uuid });

export const listItemsQuery = z.object({
  ownerType: ownerType.optional(),
  ownerId: uuid.optional(),
});
export type ListItemsQuery = z.infer<typeof listItemsQuery>;
